import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { eventsAPI, notesAPI, activitiesAPI, locationsAPI, contactsAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Separator } from '../components/ui/separator';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import { formatDistanceToNow, format, parseISO } from 'date-fns';
import html2pdf from 'html2pdf.js';
import { AVF_LOGO_BASE64 } from '../utils/avfLogoBase64';
import {
  Calendar,
  Pencil,
  Trash2,
  Building2,
  MapPin,
  Users,
  FileText,
  History,
  ArrowLeft,
  Send,
  Clock,
  Music,
  Monitor,
  Lightbulb,
  Wifi,
  Zap,
  Box,
  Cable,
  User,
  Mail,
  Phone,
  Theater,
  ExternalLink,
  Copy,
  ClipboardList,
  Download,
  FileDown,
  Loader2,
  ChevronDown,
  Truck,
} from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import CallSheetsTab from '../components/CallSheetsTab';
import EditableOverviewTab from '../components/EditableOverviewTab';
import EditableAudioTab from '../components/EditableAudioTab';
import EditableVideoTab from '../components/EditableVideoTab';
import EditableLightingTab from '../components/EditableLightingTab';
import EditableStreamingTab from '../components/EditableStreamingTab';
import EditableElectricTab from '../components/EditableElectricTab';
import EditableScenicTab from '../components/EditableScenicTab';
import EditableContactsTab from '../components/EditableContactsTab';
import EditableScheduleTab from '../components/EditableScheduleTab';
import EditableRiggingTab from '../components/EditableRiggingTab';
import EditableAncillaryRoomsTab from '../components/EditableAncillaryRoomsTab';
import LogisticsLetterTab from '../components/LogisticsLetterTab';
import EditablePostTab from '../components/EditablePostTab';
import { useAuth } from '../context/AuthContext';

const STATUS_OPTIONS = [
  { value: 'inquiry', label: 'Inquiry', color: 'border-red-500/30 bg-red-500/10 text-red-400' },
  { value: 'tentative', label: 'Tentative', color: 'border-yellow-500/30 bg-yellow-500/10 text-yellow-400' },
  { value: 'confirmed', label: 'Confirmed', color: 'border-emerald-500/30 bg-emerald-500/10 text-emerald-400' },
  { value: 'completed', label: 'Completed', color: 'border-cyan-500/30 bg-cyan-500/10 text-cyan-400' },
  { value: 'cancelled', label: 'Cancelled', color: 'border-zinc-500/30 bg-zinc-500/10 text-zinc-400' },
];

const getStatusBadge = (status) => {
  return STATUS_OPTIONS.find((s) => s.value === status) || STATUS_OPTIONS[0];
};

const formatDate = (dateStr) => {
  if (!dateStr) return null;
  try {
    return format(parseISO(dateStr), 'EEEE, MMMM d, yyyy');
  } catch {
    return dateStr;
  }
};

// Helper to check if a tab has content
const hasTabContent = (event, contacts, tabName) => {
  if (!event) return false;
  
  switch (tabName) {
    case 'overview':
      return !!(event.room || event.description);
    case 'schedule':
      return event.schedule && event.schedule.length > 0;
    case 'contacts':
      return contacts && contacts.length > 0;
    case 'audio':
      return !!(
        event.audio_overview ||
        event.audio_notes ||
        (event.audio_inputs && event.audio_inputs.length > 0) ||
        (event.audio_outputs && event.audio_outputs.length > 0)
      );
    case 'video':
      return !!(
        event.video_overview ||
        event.video_notes ||
        (event.video_inputs && event.video_inputs.length > 0) ||
        (event.video_outputs && event.video_outputs.length > 0)
      );
    case 'lighting':
      return !!event.lighting_notes;
    case 'streaming':
      // Check if streaming object has any non-empty values
      if (!event.streaming) return false;
      return !!(
        event.streaming.what ||
        event.streaming.broadcast_layout ||
        event.streaming.remote_calls ||
        event.streaming.lower_thirds_captioning ||
        event.streaming.rtmp_url ||
        event.streaming.stream_key ||
        event.streaming.audience_link ||
        event.streaming.embed_code ||
        (event.streaming.streams && event.streaming.streams.length > 0)
      );
    case 'electric':
      // Check if electric object has any non-empty values
      if (!event.electric) return false;
      return !!(
        event.electric.audio_notes ||
        event.electric.video_notes ||
        event.electric.lighting_notes ||
        event.electric.other_notes ||
        (event.electric.connections && event.electric.connections.length > 0)
      );
    case 'scenic':
      // Check if scenic_staging has any non-empty values, or rigging has content
      const hasScenic = event.scenic_staging && (
        event.scenic_staging.scenic ||
        event.scenic_staging.staging
      );
      return !!(hasScenic || event.rigging);
    case 'trucking':
      // Check if any schedule items have Trucking type with truck_assignments
      if (!event.schedule) return false;
      return event.schedule.some(item => 
        (item.schedule_types || []).includes('Trucking') && 
        item.truck_assignments && 
        item.truck_assignments.length > 0
      );
    case 'activity':
      return false; // Activity tab doesn't need a content indicator
    default:
      return false;
  }
};

// Green dot indicator component
const ContentIndicator = ({ hasContent }) => {
  if (!hasContent) return null;
  return (
    <span className="ml-1.5 inline-block w-2 h-2 rounded-full bg-emerald-500" />
  );
};

const EventDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isViewer, isAdmin } = useAuth();
  const [event, setEvent] = useState(null);
  const [location, setLocation] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [allContacts, setAllContacts] = useState([]);
  const [notes, setNotes] = useState([]);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDelete, setShowDelete] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [hasPendingChanges, setHasPendingChanges] = useState(false);
  const [showCopyDialog, setShowCopyDialog] = useState(false);
  const [copying, setCopying] = useState(false);
  const [copyForm, setCopyForm] = useState({ flex_number: '', start_date: '', end_date: '' });

  // Warn user about unsaved changes when leaving the page
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasPendingChanges) {
        e.preventDefault();
        e.returnValue = 'You have unsaved changes. Are you sure you want to leave?';
        return e.returnValue;
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasPendingChanges]);

  useEffect(() => {
    loadData();
  }, [id]);

  const loadData = async () => {
    try {
      const [eventRes, contactsRes, notesRes, activitiesRes] = await Promise.all([
        eventsAPI.get(id),
        eventsAPI.getContacts(id),
        notesAPI.list('event', id),
        activitiesAPI.list('event', id),
      ]);
      setEvent(eventRes.data);
      setContacts(contactsRes.data.contacts || []);
      setNotes(notesRes.data);
      setActivities(activitiesRes.data);
      
      // Load all contacts separately (for trucking tab driver phones)
      try {
        const allContactsRes = await contactsAPI.list({ limit: 1000 });
        // Handle paginated response format
        setAllContacts(allContactsRes.data.data || allContactsRes.data || []);
      } catch (e) {
        console.error('Failed to load all contacts:', e);
        setAllContacts([]);
      }
      
      // Load location details if location_id or venue_id exists
      const locationId = eventRes.data.location_id || eventRes.data.venue_id;
      if (locationId) {
        try {
          const locationRes = await locationsAPI.get(locationId);
          setLocation(locationRes.data);
        } catch (e) {
          console.error('Failed to load location:', e);
        }
      }
    } catch (error) {
      toast.error('Failed to load event');
      navigate('/events');
    } finally {
      setLoading(false);
    }
  };

  // Handler for inline updates from editable tabs
  const handleEventUpdate = useCallback(async (updates) => {
    if (isViewer) return;
    setHasPendingChanges(true);
    try {
      await eventsAPI.update(id, updates);
      // Update local state to reflect changes
      setEvent(prev => ({ ...prev, ...updates }));
      setHasPendingChanges(false);
    } catch (error) {
      console.error('Failed to update event:', error);
      toast.error('Failed to save changes');
      throw error;
    }
  }, [id, isViewer]);

  const handleDelete = async () => {
    try {
      await eventsAPI.delete(id);
      toast.success('Event deleted');
      navigate('/events');
    } catch (error) {
      toast.error('Failed to delete event');
    }
  };

  const handleCopyEvent = async () => {
    if (!copyForm.flex_number || !copyForm.start_date) {
      toast.error('Please enter a Flex # and start date');
      return;
    }
    setCopying(true);
    try {
      const response = await eventsAPI.copy(id, copyForm);
      toast.success(`Event copied as #${copyForm.flex_number}`);
      setShowCopyDialog(false);
      navigate(`/events/${response.data.id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to copy event');
    } finally {
      setCopying(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  // PDF Export Functions
  const generateAVFactoryHeader = () => `
    <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 16px; padding-bottom: 12px; border-bottom: 2px solid #1a1a2e;">
      <div style="display: flex; align-items: center; gap: 12px;">
        <img src="${AVF_LOGO_BASE64}" alt="AV Factory" style="height: 40px; width: auto;" />
      </div>
      <div style="text-align: right; color: #666; font-size: 10px;">
        <p style="margin: 0;">www.avfactory.com</p>
      </div>
    </div>
  `;

  const generateEventHeader = () => `
    <div style="margin-bottom: 20px;">
      <h1 style="font-size: 22px; font-weight: bold; margin: 0 0 8px 0;">${event?.event_name || ''}</h1>
      <div style="display: flex; gap: 16px; flex-wrap: wrap; font-size: 12px; color: #666;">
        ${event?.flex_number ? `<span><strong>Flex #:</strong> ${event.flex_number}</span>` : ''}
        ${event?.start_date ? `<span><strong>Date:</strong> ${formatDate(event.start_date)}${event?.end_date && event.end_date !== event.start_date ? ` - ${formatDate(event.end_date)}` : ''}</span>` : ''}
      </div>
      ${event?.client_name ? `<p style="margin: 4px 0 0 0; font-size: 12px;"><strong>Client:</strong> ${event.client_name}</p>` : ''}
      ${event?.location_name ? `<p style="margin: 4px 0 0 0; font-size: 12px;"><strong>Location:</strong> ${event.location_name}</p>` : ''}
    </div>
  `;

  const generateTitlePageHTML = () => `
    <div style="width: 8.5in; padding: 0.2in 0.5in; font-family: Arial, sans-serif; font-size: 12px; line-height: 1.4; box-sizing: border-box; background: white; color: black;">
      <div style="display: flex; flex-direction: column; justify-content: center; align-items: center; min-height: 9in;">
        <img src="${AVF_LOGO_BASE64}" alt="AV Factory" style="height: 60px; width: auto; margin-bottom: 48px;" />
        <h1 style="font-size: 32px; font-weight: bold; margin: 0 0 12px 0; text-align: center;">${event?.event_name || ''}</h1>
        ${event?.client_name ? `<p style="font-size: 16px; color: #666; margin: 8px 0; text-align: center;">${event.client_name}</p>` : ''}
        ${event?.location_name ? `<p style="font-size: 14px; color: #888; margin: 4px 0; text-align: center;">${event.location_name}</p>` : ''}
        ${event?.start_date ? `<p style="font-size: 14px; color: #888; margin: 4px 0; text-align: center;">${formatDate(event.start_date)}${event?.end_date && event.end_date !== event.start_date ? ` - ${formatDate(event.end_date)}` : ''}</p>` : ''}
        ${event?.flex_number ? `<p style="font-size: 12px; color: #aaa; margin: 4px 0; text-align: center;">Flex # ${event.flex_number}</p>` : ''}
        <div style="margin-top: 48px; text-align: center; font-size: 13px; color: #444;">
          ${event?.project_manager_name ? `<p style="margin: 6px 0;"><strong>Project Manager:</strong> ${event.project_manager_name}</p>` : ''}
          ${event?.account_exec_name ? `<p style="margin: 6px 0;"><strong>Account Executive:</strong> ${event.account_exec_name}</p>` : ''}
        </div>
        <p style="margin-top: 48px; font-size: 10px; color: #aaa;">www.avfactory.com</p>
      </div>
    </div>
  `;

  const generateSectionHTML = (title, content, addPageBreak = false) => `
    <div style="width: 8.5in; padding: 0.2in 0.5in; font-family: Arial, sans-serif; font-size: 12px; line-height: 1.4; box-sizing: border-box; background: white; color: black; ${addPageBreak ? 'page-break-before: always;' : ''}">
      ${generateAVFactoryHeader()}
      ${generateEventHeader()}
      <div style="border-top: 1px solid #ddd; padding-top: 16px;">
        <h2 style="font-size: 16px; font-weight: bold; margin: 0 0 12px 0; color: #1a1a2e;">${title}</h2>
        ${content}
      </div>
    </div>
  `;

  const generateOverviewHTML = () => {
    return `
      <div style="background: #f5f5f5; padding: 12px; border-radius: 4px;">
        <p style="font-size: 10px; color: #666; text-transform: uppercase; margin: 0 0 4px 0;">Description</p>
        <p style="margin: 0; white-space: pre-wrap;">${event?.description || 'No description provided.'}</p>
      </div>
    `;
  };

  // Generate multi-page schedule PDF (one page per day)
  const generateSchedulePagesHTML = () => {
    const schedule = event?.schedule || [];
    if (schedule.length === 0) {
      return generateSectionHTML('Production Schedule', '<p style="color: #666;">No schedule items recorded.</p>');
    }

    const highlightPdfColors = {
      yellow: '#fef08a', green: '#a7f3d0', blue: '#bfdbfe',
      red: '#fecaca', purple: '#ddd6fe', orange: '#fed7aa'
    };

    // Convert "HH:mm" to "h:mm AM/PM"
    const formatTime12 = (t) => {
      if (!t) return '-';
      const [h, m] = t.split(':').map(Number);
      if (isNaN(h)) return t;
      const suffix = h >= 12 ? 'PM' : 'AM';
      const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
      return `${h12}:${String(m).padStart(2, '0')} ${suffix}`;
    };

    const sortedSchedule = [...schedule].sort((a, b) => {
      const dateCompare = (a.date || '').localeCompare(b.date || '');
      if (dateCompare !== 0) return dateCompare;
      return (a.time || '').localeCompare(b.time || '');
    });

    const grouped = {};
    sortedSchedule.forEach(item => {
      const d = item.date || 'No Date';
      if (!grouped[d]) grouped[d] = [];
      grouped[d].push(item);
    });

    const dates = Object.keys(grouped).sort();
    const dateTags = event?.schedule_date_tags || {};

    return dates.map((date, idx) => {
      const tags = dateTags[date] || [];
      const tagsHtml = tags.length > 0
        ? `<span style="margin-left: 12px; font-size: 10px; color: #666;">${tags.join(' / ')}</span>`
        : '';
      const dateLabel = date === 'No Date' ? 'No Date' : format(parseISO(date), 'EEEE, MMMM d, yyyy');

      const rows = grouped[date].map(item => {
        const bgColor = item.highlight ? highlightPdfColors[item.highlight] || '' : '';
        const rowStyle = bgColor ? `border-bottom: 1px solid #eee; background-color: ${bgColor}; page-break-inside: avoid;` : 'border-bottom: 1px solid #eee; page-break-inside: avoid;';
        return `
        <tr style="${rowStyle}">
          <td style="padding: 6px 8px;">${formatTime12(item.time)}</td>
          <td style="padding: 6px 8px;">${item.duration ? `${item.duration} min` : '-'}</td>
          <td style="padding: 6px 8px; font-weight: 500;">${item.action || '-'}</td>
          <td style="padding: 6px 8px;">${item.location || '-'}</td>
          <td style="padding: 6px 8px; font-size: 10px;">${item.notes || ''}</td>
        </tr>`;
      }).join('');

      return `
        <div style="width: 8.5in; padding: 0.2in 0.5in; font-family: Arial, sans-serif; font-size: 12px; line-height: 1.4; box-sizing: border-box; background: white; color: black; page-break-before: always;">
          ${generateAVFactoryHeader()}
          ${generateEventHeader()}
          <div style="border-top: 1px solid #ddd; padding-top: 16px;">
            <h2 style="font-size: 16px; font-weight: bold; margin: 0 0 12px 0; color: #1a1a2e;">${dateLabel}${tagsHtml}</h2>
            <table style="width: 100%; border-collapse: collapse; font-size: 11px;">
              <thead>
                <tr style="background: #f5f5f5;">
                  <th style="text-align: left; padding: 8px; font-weight: bold; width: 80px;">Time</th>
                  <th style="text-align: left; padding: 8px; font-weight: bold; width: 55px;">Duration</th>
                  <th style="text-align: left; padding: 8px; font-weight: bold;">Action</th>
                  <th style="text-align: left; padding: 8px; font-weight: bold;">Location</th>
                  <th style="text-align: left; padding: 8px; font-weight: bold;">Notes</th>
                </tr>
              </thead>
              <tbody>${rows}</tbody>
            </table>
          </div>
        </div>
      `;
    }).join('');
  };

  // Generate continuous-flow schedule PDF (all days on one flowing document)
  const generateScheduleFlowHTML = () => {
    const schedule = event?.schedule || [];
    if (schedule.length === 0) {
      return generateSectionHTML('Production Schedule', '<p style="color: #666;">No schedule items recorded.</p>');
    }

    const highlightPdfColors = {
      yellow: '#fef08a', green: '#a7f3d0', blue: '#bfdbfe',
      red: '#fecaca', purple: '#ddd6fe', orange: '#fed7aa'
    };

    const formatTime12 = (t) => {
      if (!t) return '-';
      const [h, m] = t.split(':').map(Number);
      if (isNaN(h)) return t;
      const suffix = h >= 12 ? 'PM' : 'AM';
      const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
      return `${h12}:${String(m).padStart(2, '0')} ${suffix}`;
    };

    const sortedSchedule = [...schedule].sort((a, b) => {
      const dateCompare = (a.date || '').localeCompare(b.date || '');
      if (dateCompare !== 0) return dateCompare;
      return (a.time || '').localeCompare(b.time || '');
    });

    const grouped = {};
    sortedSchedule.forEach(item => {
      const d = item.date || 'No Date';
      if (!grouped[d]) grouped[d] = [];
      grouped[d].push(item);
    });

    const dates = Object.keys(grouped).sort();
    const dateTags = event?.schedule_date_tags || {};

    let tableContent = '';
    dates.forEach((date, idx) => {
      const tags = dateTags[date] || [];
      const tagsHtml = tags.length > 0
        ? ` <span style="font-size: 10px; font-weight: normal; color: #666;">${tags.join(' / ')}</span>`
        : '';
      const dateLabel = date === 'No Date' ? 'No Date' : format(parseISO(date), 'EEEE, MMMM d, yyyy');

      // Day header row spanning all columns
      tableContent += `
        <tr style="page-break-inside: avoid;">
          <td colspan="5" style="padding: ${idx > 0 ? '20px' : '4px'} 8px 8px 8px; border-bottom: 2px solid #1a1a2e;">
            <span style="font-size: 13px; font-weight: bold; color: #1a1a2e;">${dateLabel}</span>${tagsHtml}
          </td>
        </tr>`;

      grouped[date].forEach(item => {
        const bgColor = item.highlight ? highlightPdfColors[item.highlight] || '' : '';
        const rowStyle = bgColor ? `border-bottom: 1px solid #eee; background-color: ${bgColor}; page-break-inside: avoid;` : 'border-bottom: 1px solid #eee; page-break-inside: avoid;';
        tableContent += `
        <tr style="${rowStyle}">
          <td style="padding: 6px 8px;">${formatTime12(item.time)}</td>
          <td style="padding: 6px 8px;">${item.duration ? `${item.duration} min` : '-'}</td>
          <td style="padding: 6px 8px; font-weight: 500;">${item.action || '-'}</td>
          <td style="padding: 6px 8px;">${item.location || '-'}</td>
          <td style="padding: 6px 8px; font-size: 10px;">${item.notes || ''}</td>
        </tr>`;
      });
    });

    return `
      <div style="width: 8.5in; padding: 0.2in 0.5in; font-family: Arial, sans-serif; font-size: 12px; line-height: 1.4; box-sizing: border-box; background: white; color: black;">
        ${generateAVFactoryHeader()}
        ${generateEventHeader()}
        <div style="border-top: 1px solid #ddd; padding-top: 16px;">
          <h2 style="font-size: 16px; font-weight: bold; margin: 0 0 12px 0; color: #1a1a2e;">Production Schedule</h2>
          <table style="width: 100%; border-collapse: collapse; font-size: 11px;">
            <thead>
              <tr style="background: #f5f5f5;">
                <th style="text-align: left; padding: 8px; font-weight: bold; width: 80px;">Time</th>
                <th style="text-align: left; padding: 8px; font-weight: bold; width: 55px;">Duration</th>
                <th style="text-align: left; padding: 8px; font-weight: bold;">Action</th>
                <th style="text-align: left; padding: 8px; font-weight: bold;">Location</th>
                <th style="text-align: left; padding: 8px; font-weight: bold;">Notes</th>
              </tr>
            </thead>
            <tbody>${tableContent}</tbody>
          </table>
        </div>
      </div>
    `;
  };

  const generateContactsHTML = () => {
    // Use enriched contacts (from eventsAPI.getContacts) which have name/email/phone
    const eventContacts = contacts || [];
    if (eventContacts.length === 0) return '<p style="color: #666;">No contacts assigned.</p>';

    const contactCards = eventContacts.map(c => `
      <div style="background: #f5f5f5; padding: 12px; border-radius: 4px; margin-bottom: 8px; page-break-inside: avoid;">
        <p style="font-weight: bold; margin: 0;">${c.contact_name || 'Unknown'}</p>
        <p style="font-size: 11px; color: #666; margin: 2px 0 0 0;">${c.event_role || 'No role'}${c.organization_name ? ` - ${c.organization_name}` : ''}${c.title ? ` (${c.title})` : ''}</p>
        ${c.email ? `<p style="font-size: 11px; margin: 2px 0 0 0;">Email: ${c.email}</p>` : ''}
        ${c.phone ? `<p style="font-size: 11px; margin: 2px 0 0 0;">Phone: ${c.phone}</p>` : ''}
        ${c.mobile ? `<p style="font-size: 11px; margin: 2px 0 0 0;">Mobile: ${c.mobile}</p>` : ''}
      </div>
    `).join('');

    return contactCards;
  };

  const generateAudioHTML = () => {
    let content = '';
    if (event?.audio_overview) {
      content += `<div style="background: #f5f5f5; padding: 12px; border-radius: 4px; margin-bottom: 12px;"><p style="font-size: 10px; color: #666; text-transform: uppercase; margin: 0 0 4px 0;">Overview</p><p style="margin: 0; white-space: pre-wrap;">${event.audio_overview}</p></div>`;
    }
    if (event?.audio_inputs?.length > 0) {
      const rows = event.audio_inputs.map((i, idx) => `<tr style="border-bottom: 1px solid #eee; page-break-inside: avoid;"><td style="padding: 6px; text-align: center; font-family: monospace;">${i.input_number || idx + 1}</td><td style="padding: 6px;">${i.description || '-'}</td><td style="padding: 6px;">${i.location || '-'}</td><td style="padding: 6px;">${i.notes || '-'}</td></tr>`).join('');
      content += `<div style="margin-bottom: 12px;"><p style="font-weight: bold; margin: 0 0 8px 0;">Inputs</p><table style="width: 100%; border-collapse: collapse; font-size: 11px;"><thead><tr style="background: #f5f5f5;"><th style="text-align: center; padding: 6px; width: 40px;">#</th><th style="text-align: left; padding: 6px;">Description</th><th style="text-align: left; padding: 6px;">Location</th><th style="text-align: left; padding: 6px;">Notes</th></tr></thead><tbody>${rows}</tbody></table></div>`;
    }
    if (event?.audio_outputs?.length > 0) {
      const rows = event.audio_outputs.map((o, idx) => `<tr style="border-bottom: 1px solid #eee; page-break-inside: avoid;"><td style="padding: 6px; text-align: center; font-family: monospace;">${o.output_number || idx + 1}</td><td style="padding: 6px;">${o.description || '-'}</td><td style="padding: 6px;">${o.location || '-'}</td><td style="padding: 6px;">${o.notes || '-'}</td></tr>`).join('');
      content += `<div style="margin-bottom: 12px;"><p style="font-weight: bold; margin: 0 0 8px 0;">Outputs</p><table style="width: 100%; border-collapse: collapse; font-size: 11px;"><thead><tr style="background: #f5f5f5;"><th style="text-align: center; padding: 6px; width: 40px;">#</th><th style="text-align: left; padding: 6px;">Description</th><th style="text-align: left; padding: 6px;">Location</th><th style="text-align: left; padding: 6px;">Notes</th></tr></thead><tbody>${rows}</tbody></table></div>`;
    }
    if (event?.audio_intercoms?.length > 0) {
      const rows = event.audio_intercoms.map((ic, idx) => `<tr style="border-bottom: 1px solid #eee; page-break-inside: avoid;"><td style="padding: 6px; text-align: center; font-family: monospace;">${ic.com_number || idx + 1}</td><td style="padding: 6px;">${ic.user || '-'}</td><td style="padding: 6px;">${ic.type || '-'}</td><td style="padding: 6px;">${ic.location || '-'}</td><td style="padding: 6px;">${ic.channels || '-'}</td><td style="padding: 6px;">${ic.notes || '-'}</td></tr>`).join('');
      content += `<div style="margin-bottom: 12px;"><p style="font-weight: bold; margin: 0 0 8px 0;">Intercom</p><table style="width: 100%; border-collapse: collapse; font-size: 11px;"><thead><tr style="background: #f5f5f5;"><th style="text-align: center; padding: 6px; width: 40px;">#</th><th style="text-align: left; padding: 6px;">User</th><th style="text-align: left; padding: 6px;">Type</th><th style="text-align: left; padding: 6px;">Location</th><th style="text-align: left; padding: 6px;">Channels</th><th style="text-align: left; padding: 6px;">Notes</th></tr></thead><tbody>${rows}</tbody></table></div>`;
    }
    if (event?.audio_notes) {
      content += `<div style="background: #fff8e1; padding: 12px; border-radius: 4px;"><p style="font-size: 10px; color: #666; text-transform: uppercase; margin: 0 0 4px 0;">Notes</p><p style="margin: 0; white-space: pre-wrap;">${event.audio_notes}</p></div>`;
    }
    return content || '<p style="color: #666;">No audio information recorded.</p>';
  };

  const generateVideoHTML = () => {
    let content = '';
    if (event?.video_overview) {
      content += `<div style="background: #f5f5f5; padding: 12px; border-radius: 4px; margin-bottom: 12px;"><p style="font-size: 10px; color: #666; text-transform: uppercase; margin: 0 0 4px 0;">Overview</p><p style="margin: 0; white-space: pre-wrap;">${event.video_overview}</p></div>`;
    }
    if (event?.video_inputs?.length > 0) {
      const rows = event.video_inputs.map((i, idx) => `<tr style="border-bottom: 1px solid #eee; page-break-inside: avoid;"><td style="padding: 6px; text-align: center; font-family: monospace;">${i.input_number || idx + 1}</td><td style="padding: 6px;">${i.description || '-'}</td><td style="padding: 6px;">${i.source || '-'}</td><td style="padding: 6px;">${i.resolution || '-'}</td><td style="padding: 6px;">${i.video_connector || '-'}</td><td style="padding: 6px;">${i.location || '-'}</td><td style="padding: 6px;">${i.notes || '-'}</td></tr>`).join('');
      content += `<div style="margin-bottom: 12px;"><p style="font-weight: bold; margin: 0 0 8px 0;">Inputs</p><table style="width: 100%; border-collapse: collapse; font-size: 11px;"><thead><tr style="background: #f5f5f5;"><th style="text-align: center; padding: 6px; width: 40px;">#</th><th style="text-align: left; padding: 6px;">Description</th><th style="text-align: left; padding: 6px;">Device</th><th style="text-align: left; padding: 6px;">Resolution</th><th style="text-align: left; padding: 6px;">Connector</th><th style="text-align: left; padding: 6px;">Location</th><th style="text-align: left; padding: 6px;">Notes</th></tr></thead><tbody>${rows}</tbody></table></div>`;
    }
    if (event?.video_outputs?.length > 0) {
      const rows = event.video_outputs.map((o, idx) => `<tr style="border-bottom: 1px solid #eee; page-break-inside: avoid;"><td style="padding: 6px; text-align: center; font-family: monospace;">${o.output_number || idx + 1}</td><td style="padding: 6px;">${o.description || '-'}</td><td style="padding: 6px;">${o.source || '-'}</td><td style="padding: 6px;">${o.resolution || '-'}</td><td style="padding: 6px;">${o.video_connector || '-'}</td><td style="padding: 6px;">${o.location || '-'}</td><td style="padding: 6px;">${o.notes || '-'}</td></tr>`).join('');
      content += `<div style="margin-bottom: 12px;"><p style="font-weight: bold; margin: 0 0 8px 0;">Outputs</p><table style="width: 100%; border-collapse: collapse; font-size: 11px;"><thead><tr style="background: #f5f5f5;"><th style="text-align: center; padding: 6px; width: 40px;">#</th><th style="text-align: left; padding: 6px;">Description</th><th style="text-align: left; padding: 6px;">Device</th><th style="text-align: left; padding: 6px;">Resolution</th><th style="text-align: left; padding: 6px;">Connector</th><th style="text-align: left; padding: 6px;">Location</th><th style="text-align: left; padding: 6px;">Notes</th></tr></thead><tbody>${rows}</tbody></table></div>`;
    }
    if (event?.video_notes) {
      content += `<div style="background: #fff8e1; padding: 12px; border-radius: 4px;"><p style="font-size: 10px; color: #666; text-transform: uppercase; margin: 0 0 4px 0;">Notes</p><p style="margin: 0; white-space: pre-wrap;">${event.video_notes}</p></div>`;
    }
    return content || '<p style="color: #666;">No video information recorded.</p>';
  };

  const generateLightingHTML = () => {
    if (!event?.lighting_notes) return '<p style="color: #666;">No lighting information recorded.</p>';
    return `<div style="background: #f5f5f5; padding: 12px; border-radius: 4px;"><p style="margin: 0; white-space: pre-wrap;">${event.lighting_notes}</p></div>`;
  };

  const generateStreamingHTML = () => {
    const s = event?.streaming;
    if (!s || (!s.what && !s.rtmp_url && !s.stream_key && !(s.streams?.length > 0))) return '<p style="color: #666;">No streaming information recorded.</p>';

    const formatTime12 = (t) => {
      if (!t) return '';
      const [h, m] = t.split(':').map(Number);
      if (isNaN(h)) return t;
      const suffix = h >= 12 ? 'PM' : 'AM';
      const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
      return `${h12}:${String(m).padStart(2, '0')} ${suffix}`;
    };
    
    let content = '<div style="display: grid; grid-template-columns: 1fr 1fr; gap: 12px;">';
    if (s.what) content += `<div style="background: #f5f5f5; padding: 12px; border-radius: 4px;"><p style="font-size: 10px; color: #666; text-transform: uppercase; margin: 0 0 4px 0;">What</p><p style="margin: 0;">${s.what}</p></div>`;
    if (s.broadcast_layout) content += `<div style="background: #f5f5f5; padding: 12px; border-radius: 4px;"><p style="font-size: 10px; color: #666; text-transform: uppercase; margin: 0 0 4px 0;">Broadcast Layout</p><p style="margin: 0;">${s.broadcast_layout}</p></div>`;
    if (s.remote_calls) content += `<div style="background: #f5f5f5; padding: 12px; border-radius: 4px;"><p style="font-size: 10px; color: #666; text-transform: uppercase; margin: 0 0 4px 0;">Remote Calls</p><p style="margin: 0;">${s.remote_calls}</p></div>`;
    if (s.lower_thirds_captioning) content += `<div style="background: #f5f5f5; padding: 12px; border-radius: 4px;"><p style="font-size: 10px; color: #666; text-transform: uppercase; margin: 0 0 4px 0;">Lower Thirds/Captioning</p><p style="margin: 0;">${s.lower_thirds_captioning}</p></div>`;
    content += '</div>';

    // Legacy single stream fields (backwards compat)
    if (s.rtmp_url || s.stream_key || s.audience_link) {
      content += '<div style="margin-top: 12px; background: #e3f2fd; padding: 12px; border-radius: 4px; page-break-inside: avoid;">';
      content += '<p style="font-weight: bold; margin: 0 0 8px 0;">Stream Configuration</p>';
      if (s.rtmp_url) content += `<p style="font-size: 11px; margin: 4px 0;"><strong>RTMP URL:</strong> <span style="font-family: monospace;">${s.rtmp_url}</span></p>`;
      if (s.stream_key) content += `<p style="font-size: 11px; margin: 4px 0;"><strong>Stream Key:</strong> <span style="font-family: monospace;">${s.stream_key}</span></p>`;
      if (s.audience_link) content += `<p style="font-size: 11px; margin: 4px 0;"><strong>Audience Link:</strong> ${s.audience_link}</p>`;
      content += '</div>';
    }

    // Stream entries
    if (s.streams?.length > 0) {
      s.streams.forEach((stream, idx) => {
        const startStr = [stream.start_date ? format(parseISO(stream.start_date), 'MMM d') : '', formatTime12(stream.start_time)].filter(Boolean).join(' ');
        const endStr = [stream.end_date ? format(parseISO(stream.end_date), 'MMM d') : '', formatTime12(stream.end_time)].filter(Boolean).join(' ');
        const timeRange = startStr || endStr
          ? ` (${startStr || '?'} - ${endStr || '?'})`
          : '';

        content += `<div style="margin-top: 12px; border: 1px solid #ddd; border-radius: 4px; padding: 12px; page-break-inside: avoid;">`;
        content += `<p style="font-weight: bold; font-size: 12px; margin: 0 0 8px 0;">${stream.description || 'Stream ' + (idx + 1)}${timeRange}</p>`;
        
        let details = '';
        if (stream.rtmp_url) details += `<p style="font-size: 11px; margin: 3px 0;"><strong>RTMP URL:</strong> <span style="font-family: monospace;">${stream.rtmp_url}</span></p>`;
        if (stream.stream_key) details += `<p style="font-size: 11px; margin: 3px 0;"><strong>Stream Key:</strong> <span style="font-family: monospace;">${stream.stream_key}</span></p>`;
        if (stream.backup_url) details += `<p style="font-size: 11px; margin: 3px 0;"><strong>Backup URL:</strong> <span style="font-family: monospace;">${stream.backup_url}</span></p>`;
        if (stream.audience_link) details += `<p style="font-size: 11px; margin: 3px 0;"><strong>Audience Link:</strong> ${stream.audience_link}</p>`;
        if (stream.embed_code) details += `<p style="font-size: 11px; margin: 3px 0;"><strong>Embed Code:</strong> <span style="font-family: monospace; font-size: 10px;">${stream.embed_code}</span></p>`;
        if (stream.notes) details += `<p style="font-size: 11px; margin: 8px 0 0 0; font-style: italic; color: #666;">${stream.notes}</p>`;

        content += details || '<p style="font-size: 11px; color: #666;">No details configured.</p>';
        content += '</div>';
      });
    }

    return content;
  };

  const generateElectricHTML = () => {
    const e = event?.electric;
    if (!e || (!e.connections?.length && !e.distros?.length && !e.audio_notes && !e.video_notes && !e.lighting_notes && !e.other_notes)) {
      return '<p style="color: #666;">No electrical information recorded.</p>';
    }
    
    let content = '';
    if (e.connections?.length > 0) {
      const rows = e.connections.map(c => `<tr style="border-bottom: 1px solid #eee; page-break-inside: avoid;"><td style="padding: 6px;">${c.connector_type || '-'}</td><td style="padding: 6px;">${c.amperage || '-'}</td><td style="padding: 6px;">${c.voltage || '-'}</td><td style="padding: 6px;">${c.phase || '-'}</td><td style="padding: 6px;">${c.connection_location || '-'}</td><td style="padding: 6px;">${Array.isArray(c.for_departments) ? c.for_departments.join(', ') : (c.for_departments || '-')}</td></tr>`).join('');
      content += `<div style="margin-bottom: 12px;"><p style="font-weight: bold; margin: 0 0 8px 0;">Available Electrical Service</p><table style="width: 100%; border-collapse: collapse; font-size: 11px;"><thead><tr style="background: #f5f5f5;"><th style="text-align: left; padding: 6px;">Connector</th><th style="text-align: left; padding: 6px;">Amps</th><th style="text-align: left; padding: 6px;">Voltage</th><th style="text-align: left; padding: 6px;">Phase</th><th style="text-align: left; padding: 6px;">Location</th><th style="text-align: left; padding: 6px;">For Departments</th></tr></thead><tbody>${rows}</tbody></table></div>`;
    }
    
    const notes = [];
    if (e.audio_notes) notes.push(`<strong>Audio:</strong> ${e.audio_notes}`);
    if (e.video_notes) notes.push(`<strong>Video:</strong> ${e.video_notes}`);
    if (e.lighting_notes) notes.push(`<strong>Lighting:</strong> ${e.lighting_notes}`);
    if (e.other_notes) notes.push(`<strong>Other:</strong> ${e.other_notes}`);
    
    if (notes.length > 0) {
      content += `<div style="background: #fff8e1; padding: 12px; border-radius: 4px; margin-bottom: 12px;"><p style="font-size: 10px; color: #666; text-transform: uppercase; margin: 0 0 8px 0;">Department Notes</p>${notes.map(n => `<p style="margin: 4px 0; font-size: 11px;">${n}</p>`).join('')}</div>`;
    }

    // Distro summary on main page
    if (e.distros?.length > 0) {
      content += '<div style="margin-top: 12px;"><p style="font-weight: bold; margin: 0 0 8px 0;">Electrical Distribution Summary</p>';
      const distroRows = e.distros.map(d => {
        const outputCount = (d.outputs || []).length;
        return `<tr style="border-bottom: 1px solid #eee; page-break-inside: avoid;"><td style="padding: 6px; font-weight: 500;">${d.distro_id || '-'}</td><td style="padding: 6px;">${d.location || '-'}</td><td style="padding: 6px;">${d.input_type || '-'}</td><td style="padding: 6px;">${d.amps || '-'}A</td><td style="padding: 6px;">${d.voltage || '-'}V</td><td style="padding: 6px;">${d.phase || '-'}</td><td style="padding: 6px;">${outputCount} circuits</td></tr>`;
      }).join('');
      content += `<table style="width: 100%; border-collapse: collapse; font-size: 11px;"><thead><tr style="background: #f5f5f5;"><th style="text-align: left; padding: 6px;">Distro</th><th style="text-align: left; padding: 6px;">Location</th><th style="text-align: left; padding: 6px;">Input</th><th style="text-align: left; padding: 6px;">Amps</th><th style="text-align: left; padding: 6px;">Voltage</th><th style="text-align: left; padding: 6px;">Phase</th><th style="text-align: left; padding: 6px;">Outputs</th></tr></thead><tbody>${distroRows}</tbody></table>`;
      content += '<p style="font-size: 10px; color: #666; margin-top: 8px; font-style: italic;">See individual distro sheets for detailed circuit assignments.</p></div>';
    }

    return content;
  };

  // Generate individual distro page for PDF (one per distro)
  const generateDistroPagesHTML = () => {
    const e = event?.electric;
    if (!e?.distros?.length) return '';

    const SOCA_PHASE = ['A', 'B', 'C', 'A', 'B', 'C'];
    const DEFAULT_EDISON = 5;

    return e.distros.map(distro => {
      const outputs = distro.outputs || [];
      const phases = { A: 0, B: 0, C: 0 };
      let totalLoad = 0;

      outputs.forEach(out => {
        const load = parseFloat(out.estimated_load) || (out.connection_type === 'Edison' ? DEFAULT_EDISON : 0);
        totalLoad += load;
        if (out.connection_type === 'L21-30') {
          phases.A += load / 3; phases.B += load / 3; phases.C += load / 3;
        } else if (out.connection_type === 'Socapex') {
          const chIdx = (parseInt(out.channel) - 1) || 0;
          const ph = SOCA_PHASE[chIdx % 6] || 'A';
          phases[ph] += load;
        } else {
          phases[out.phase || 'A'] += load;
        }
      });

      const inputAmps = parseFloat(distro.amps) || 0;
      const maxPhase = Math.max(phases.A, phases.B, phases.C);
      const utilization = inputAmps > 0 ? Math.round((maxPhase / inputAmps) * 100) : 0;

      const outputRows = outputs.map(out => {
        let autoPhase = out.phase || 'A';
        if (out.connection_type === 'Socapex') autoPhase = SOCA_PHASE[(parseInt(out.channel) - 1 || 0) % 6];
        if (out.connection_type === 'L21-30') autoPhase = '3Ø';

        return `<tr style="border-bottom: 1px solid #eee; page-break-inside: avoid;">
          <td style="padding: 5px 6px; font-family: monospace; text-align: center;">${out.channel || '-'}</td>
          <td style="padding: 5px 6px;">${out.connection_type || '-'}</td>
          <td style="padding: 5px 6px; font-family: monospace;">${out.breaker || '-'}</td>
          <td style="padding: 5px 6px; font-family: monospace; font-weight: bold;">${autoPhase}</td>
          <td style="padding: 5px 6px;">${out.destination || '-'}</td>
          <td style="padding: 5px 6px;">${out.department || '-'}</td>
          <td style="padding: 5px 6px; font-family: monospace; text-align: right;">${out.estimated_load || '-'}</td>
          <td style="padding: 5px 6px; font-size: 10px;">${out.cable_length || '-'}</td>
          <td style="padding: 5px 6px; font-size: 10px;">${out.notes || ''}</td>
        </tr>`;
      }).join('');

      return `
        <div style="width: 8.5in; padding: 0.2in 0.5in; font-family: Arial, sans-serif; font-size: 12px; line-height: 1.4; box-sizing: border-box; background: white; color: black; page-break-before: always;">
          ${generateAVFactoryHeader()}
          ${generateEventHeader()}
          <div style="border-top: 1px solid #ddd; padding-top: 16px;">
            <h2 style="font-size: 16px; font-weight: bold; margin: 0 0 4px 0; color: #1a1a2e;">Distro: ${distro.distro_id || 'Unnamed'}</h2>
            <div style="display: flex; gap: 24px; margin-bottom: 12px; font-size: 11px; color: #666;">
              <span><strong>Location:</strong> ${distro.location || '-'}</span>
              <span><strong>Input:</strong> ${distro.input_type || '-'}</span>
              <span><strong>Service:</strong> ${distro.amps || '-'}A @ ${distro.voltage || '-'}V ${distro.phase || '-'}</span>
              ${distro.disconnect_location ? `<span><strong>Disconnect:</strong> ${distro.disconnect_location}</span>` : ''}
            </div>

            <div style="display: flex; gap: 12px; margin-bottom: 16px; font-size: 11px; flex-wrap: wrap;">
              ${distro.phase !== 'Single' ? `
              <div style="background: #ffebee; padding: 8px 12px; border-radius: 4px;"><strong>Phase A:</strong> ${Math.round(phases.A * 10) / 10}A</div>
              <div style="background: #e3f2fd; padding: 8px 12px; border-radius: 4px;"><strong>Phase B:</strong> ${Math.round(phases.B * 10) / 10}A</div>
              <div style="background: #e8f5e9; padding: 8px 12px; border-radius: 4px;"><strong>Phase C:</strong> ${Math.round(phases.C * 10) / 10}A</div>
              ` : ''}
              <div style="background: #f5f5f5; padding: 8px 12px; border-radius: 4px;"><strong>Total:</strong> ${Math.round(totalLoad * 10) / 10}A${(() => { const v = parseFloat(distro.voltage) || (distro.voltage === '208/240' ? 208 : 0); if (v > 0) { const w = Math.round(totalLoad * v); return ` (${w >= 1000 ? (w/1000).toFixed(1) + 'kW' : w + 'W'})`; } return ''; })()}</div>
              <div style="background: ${utilization >= 100 ? '#ffebee' : utilization >= 80 ? '#fff8e1' : '#e8f5e9'}; padding: 8px 12px; border-radius: 4px; font-weight: bold;">${utilization}% utilized</div>
            </div>

            ${outputs.length > 0 ? `
              <table style="width: 100%; border-collapse: collapse; font-size: 11px;">
                <thead>
                  <tr style="background: #f5f5f5;">
                    <th style="text-align: center; padding: 6px; width: 40px;">Ch</th>
                    <th style="text-align: left; padding: 6px;">Connection</th>
                    <th style="text-align: left; padding: 6px;">Breaker</th>
                    <th style="text-align: left; padding: 6px; width: 40px;">Phase</th>
                    <th style="text-align: left; padding: 6px;">Destination</th>
                    <th style="text-align: left; padding: 6px;">Dept</th>
                    <th style="text-align: right; padding: 6px;">Load (A)</th>
                    <th style="text-align: left; padding: 6px;">Cable</th>
                    <th style="text-align: left; padding: 6px;">Notes</th>
                  </tr>
                </thead>
                <tbody>${outputRows}</tbody>
              </table>
            ` : '<p style="color: #666;">No output circuits configured.</p>'}
          </div>
        </div>
      `;
    }).join('');
  };

  const generateScenicHTML = () => {
    const s = event?.scenic_staging;
    if (!s || (!s.scenic && !s.staging)) return '<p style="color: #666;">No scenic/staging information recorded.</p>';
    
    let content = '';
    if (s.scenic) content += `<div style="background: #f5f5f5; padding: 12px; border-radius: 4px; margin-bottom: 12px;"><p style="font-size: 10px; color: #666; text-transform: uppercase; margin: 0 0 4px 0;">Scenic</p><p style="margin: 0; white-space: pre-wrap;">${s.scenic}</p></div>`;
    if (s.staging) content += `<div style="background: #f5f5f5; padding: 12px; border-radius: 4px;"><p style="font-size: 10px; color: #666; text-transform: uppercase; margin: 0 0 4px 0;">Staging</p><p style="margin: 0; white-space: pre-wrap;">${s.staging}</p></div>`;
    return content;
  };

  const generateRiggingHTML = () => {
    const rigging = event?.rigging;
    if (!rigging) return '<p style="color: #666;">No rigging information recorded.</p>';
    return `<div style="background: #f5f5f5; padding: 12px; border-radius: 4px; white-space: pre-wrap;">${rigging}</div>`;
  };

  const generateAncillaryRoomsHTML = () => {
    const rooms = event?.ancillary_rooms || [];
    if (rooms.length === 0) return '<p style="color: #666;">No ancillary rooms recorded.</p>';
    const rows = rooms.map((r, idx) => `<tr style="border-bottom: 1px solid #eee; page-break-inside: avoid;"><td style="padding: 6px; text-align: center; font-family: monospace;">${idx + 1}</td><td style="padding: 6px; font-weight: 500;">${r.space || '-'}</td><td style="padding: 6px;">${r.description || '-'}</td></tr>`).join('');
    return `<table style="width: 100%; border-collapse: collapse; font-size: 11px;"><thead><tr style="background: #f5f5f5;"><th style="text-align: center; padding: 6px; width: 40px;">#</th><th style="text-align: left; padding: 6px;">Space</th><th style="text-align: left; padding: 6px;">Description</th></tr></thead><tbody>${rows}</tbody></table>`;
  };

  const generateTruckingHTML = () => {
    const schedule = event?.schedule || [];
    const truckingItems = schedule.filter(i => (i.schedule_types || []).includes('Trucking'));
    if (truckingItems.length === 0) return '<p style="color: #666;">No trucking items in schedule.</p>';

    const formatTime12 = (t) => {
      if (!t) return '-';
      const [h, m] = t.split(':').map(Number);
      if (isNaN(h)) return t;
      const suffix = h >= 12 ? 'PM' : 'AM';
      const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
      return `${h12}:${String(m).padStart(2, '0')} ${suffix}`;
    };

    const sorted = [...truckingItems].sort((a, b) => {
      const dc = (a.date || '').localeCompare(b.date || '');
      return dc !== 0 ? dc : (a.time || '').localeCompare(b.time || '');
    });

    // Group by date
    const grouped = {};
    sorted.forEach(item => {
      const d = item.date || 'No Date';
      if (!grouped[d]) grouped[d] = [];
      grouped[d].push(item);
    });

    return Object.keys(grouped).sort().map(date => {
      const dateLabel = date === 'No Date' ? 'No Date' : format(parseISO(date), 'EEEE, MMMM d, yyyy');
      const rows = grouped[date].map(item => {
        const assignments = (item.truck_assignments || []).filter(a => a.truck || a.driver_name);
        const truckStr = assignments.map(a => [a.truck, a.driver_name].filter(Boolean).join(' - ')).join(', ') || '-';
        return `<tr style="border-bottom: 1px solid #eee; page-break-inside: avoid;">
          <td style="padding: 6px;">${formatTime12(item.time)}</td>
          <td style="padding: 6px; font-weight: 500;">${item.action || '-'}</td>
          <td style="padding: 6px;">${item.location || '-'}</td>
          <td style="padding: 6px;">${truckStr}</td>
          <td style="padding: 6px; font-size: 10px;">${item.notes || ''}</td>
        </tr>`;
      }).join('');

      return `
        <div style="margin-bottom: 16px;">
          <p style="font-weight: bold; margin: 0 0 8px 0;">${dateLabel}</p>
          <table style="width: 100%; border-collapse: collapse; font-size: 11px;">
            <thead><tr style="background: #f5f5f5;">
              <th style="text-align: left; padding: 6px; width: 80px;">Time</th>
              <th style="text-align: left; padding: 6px;">Action</th>
              <th style="text-align: left; padding: 6px;">Location</th>
              <th style="text-align: left; padding: 6px;">Truck / Driver</th>
              <th style="text-align: left; padding: 6px;">Notes</th>
            </tr></thead>
            <tbody>${rows}</tbody>
          </table>
        </div>`;
    }).join('');
  };

  const generateLocationHTML = () => {
    if (!location) return '<p style="color: #666;">No location information available.</p>';
    
    const locType = location.location_type ? location.location_type.charAt(0).toUpperCase() + location.location_type.slice(1) : '';
    
    let content = `
      <div style="background: #f5f5f5; padding: 12px; border-radius: 4px; margin-bottom: 12px; page-break-inside: avoid;">
        <h3 style="font-size: 14px; font-weight: bold; margin: 0 0 4px 0;">${location.location_name}</h3>
        ${locType ? `<p style="margin: 0 0 4px 0; font-size: 11px; color: #666;">${locType}${location.organization_name ? ` - ${location.organization_name}` : ''}</p>` : ''}
        ${location.address ? `<p style="margin: 4px 0; font-size: 11px;">${location.address}${location.city ? `, ${location.city}` : ''}${location.state ? ` ${location.state}` : ''}${location.zip_code ? ` ${location.zip_code}` : ''}</p>` : ''}
        ${location.phone ? `<p style="margin: 4px 0; font-size: 11px;">Phone: ${location.phone}</p>` : ''}
      </div>
    `;
    
    // Load-in info
    if (location.load_in_type?.length > 0 || location.load_in_address || location.dock_notes || location.push_notes) {
      content += '<div style="margin-bottom: 12px; page-break-inside: avoid;"><p style="font-weight: bold; margin: 0 0 8px 0;">Load-In / Logistics</p>';
      if (location.load_in_type?.length > 0) content += `<p style="font-size: 11px; margin: 4px 0;"><strong>Type:</strong> ${location.load_in_type.join(', ')}</p>`;
      if (location.load_in_address) content += `<p style="font-size: 11px; margin: 4px 0;"><strong>Address:</strong> ${location.load_in_address}</p>`;
      if (location.dock_measurements) content += `<p style="font-size: 11px; margin: 4px 0;"><strong>Dock Measurements:</strong> ${location.dock_measurements}</p>`;
      if (location.dock_notes) content += `<p style="font-size: 11px; margin: 4px 0;"><strong>Dock Notes:</strong> ${location.dock_notes}</p>`;
      if (location.elevator_sizes) content += `<p style="font-size: 11px; margin: 4px 0;"><strong>Elevator Sizes:</strong> ${location.elevator_sizes}</p>`;
      if (location.push_notes) content += `<p style="font-size: 11px; margin: 4px 0;"><strong>Push Notes:</strong> ${location.push_notes}</p>`;
      content += '</div>';
    }

    // Technical Notes
    if (location.rigging_notes || location.power_notes || location.internet_notes || location.available_equipment) {
      content += '<div style="margin-bottom: 12px; page-break-inside: avoid;"><p style="font-weight: bold; margin: 0 0 8px 0;">Technical Notes</p>';
      if (location.rigging_notes) content += `<div style="margin-bottom: 8px;"><p style="font-size: 11px; margin: 0;"><strong>Rigging:</strong></p><p style="font-size: 11px; margin: 2px 0; white-space: pre-wrap;">${location.rigging_notes}</p></div>`;
      if (location.power_notes) content += `<div style="margin-bottom: 8px;"><p style="font-size: 11px; margin: 0;"><strong>Power:</strong></p><p style="font-size: 11px; margin: 2px 0; white-space: pre-wrap;">${location.power_notes}</p></div>`;
      if (location.internet_notes) content += `<div style="margin-bottom: 8px;"><p style="font-size: 11px; margin: 0;"><strong>Internet / Streaming:</strong></p><p style="font-size: 11px; margin: 2px 0; white-space: pre-wrap;">${location.internet_notes}</p></div>`;
      if (location.available_equipment) content += `<div style="margin-bottom: 8px;"><p style="font-size: 11px; margin: 0;"><strong>Built in / Available Equipment:</strong></p><p style="font-size: 11px; margin: 2px 0; white-space: pre-wrap;">${location.available_equipment}</p></div>`;
      content += '</div>';
    }
    
    // Vendors
    if (location.av_vendor || location.rigging_vendor || location.power_vendor || location.internet_vendor) {
      content += '<div style="margin-bottom: 12px; page-break-inside: avoid;"><p style="font-weight: bold; margin: 0 0 8px 0;">House Vendors</p>';
      if (location.av_vendor) content += `<p style="font-size: 11px; margin: 4px 0;"><strong>AV:</strong> ${location.av_vendor}</p>`;
      if (location.rigging_vendor) content += `<p style="font-size: 11px; margin: 4px 0;"><strong>Rigging:</strong> ${location.rigging_vendor}</p>`;
      if (location.power_vendor) content += `<p style="font-size: 11px; margin: 4px 0;"><strong>Power:</strong> ${location.power_vendor}</p>`;
      if (location.internet_vendor) content += `<p style="font-size: 11px; margin: 4px 0;"><strong>Internet:</strong> ${location.internet_vendor}</p>`;
      content += '</div>';
    }

    // Union Requirements
    if (location.union_requirements) {
      content += `<div style="margin-bottom: 12px; background: #fff8e1; padding: 12px; border-radius: 4px; page-break-inside: avoid;"><p style="font-size: 11px; font-weight: bold; margin: 0 0 4px 0;">Union / Labor Requirements</p><p style="font-size: 11px; margin: 0; white-space: pre-wrap;">${location.union_requirements}</p></div>`;
    }
    
    // Parking & Emergency
    if (location.parking_options || location.nearest_er_name) {
      content += '<div style="display: flex; gap: 12px; margin-bottom: 12px; page-break-inside: avoid;">';
      if (location.parking_options) {
        content += `<div style="flex: 1; background: #f5f5f5; padding: 12px; border-radius: 4px;"><p style="font-size: 10px; color: #666; text-transform: uppercase; margin: 0 0 4px 0;">Parking</p><p style="font-size: 11px; margin: 0; white-space: pre-wrap;">${location.parking_options}</p></div>`;
      }
      if (location.nearest_er_name) {
        content += `<div style="flex: 1; background: #ffebee; padding: 12px; border-radius: 4px;"><p style="font-size: 10px; color: #c62828; text-transform: uppercase; margin: 0 0 4px 0;">Nearest Emergency Room</p><p style="font-weight: bold; margin: 0;">${location.nearest_er_name}</p>${location.nearest_er_address ? `<p style="font-size: 11px; margin: 2px 0 0 0;">${location.nearest_er_address}</p>` : ''}${location.nearest_er_phone ? `<p style="font-size: 11px; margin: 2px 0 0 0;">Tel: ${location.nearest_er_phone}</p>` : ''}</div>`;
      }
      content += '</div>';
    }

    // Other Notes & Lessons Learned
    if (location.other_notes || location.lessons_learned) {
      if (location.other_notes) {
        content += `<div style="margin-bottom: 12px; page-break-inside: avoid;"><p style="font-weight: bold; margin: 0 0 4px 0; font-size: 11px;">Other Notes</p><p style="font-size: 11px; margin: 0; white-space: pre-wrap;">${location.other_notes}</p></div>`;
      }
      if (location.lessons_learned) {
        content += `<div style="margin-bottom: 12px; page-break-inside: avoid;"><p style="font-weight: bold; margin: 0 0 4px 0; font-size: 11px;">Lessons Learned</p><p style="font-size: 11px; margin: 0; white-space: pre-wrap;">${location.lessons_learned}</p></div>`;
      }
    }
    
    return content;
  };

  const exportPDF = async (section) => {
    setExporting(true);
    try {
      let html = '';
      let filename = `${event?.event_name?.replace(/[^a-zA-Z0-9]/g, '_') || 'Event'}`;
      
      switch (section) {
        case 'overview':
          html = generateSectionHTML('Overview', generateOverviewHTML());
          filename += '_Overview';
          break;
        case 'titlepage':
          html = generateTitlePageHTML();
          filename += '_TitlePage';
          break;
        case 'schedule':
          html = generateSchedulePagesHTML();
          filename += '_Schedule';
          break;
        case 'schedule-flow':
          html = generateScheduleFlowHTML();
          filename += '_Schedule_Flow';
          break;
        case 'contacts':
          html = generateSectionHTML('Event Contacts', generateContactsHTML());
          filename += '_Contacts';
          break;
        case 'audio':
          html = generateSectionHTML('Audio', generateAudioHTML());
          filename += '_Audio';
          break;
        case 'video':
          html = generateSectionHTML('Video', generateVideoHTML());
          filename += '_Video';
          break;
        case 'lighting':
          html = generateSectionHTML('Lighting', generateLightingHTML());
          filename += '_Lighting';
          break;
        case 'streaming':
          html = generateSectionHTML('Streaming', generateStreamingHTML());
          filename += '_Streaming';
          break;
        case 'electric':
          html = generateSectionHTML('Electrical', generateElectricHTML());
          html += generateDistroPagesHTML();
          filename += '_Electric';
          break;
        case 'scenic':
          html = generateSectionHTML('Scenic / Staging', generateScenicHTML());
          filename += '_Scenic';
          break;
        case 'rigging':
          html = generateSectionHTML('Rigging', generateRiggingHTML());
          filename += '_Rigging';
          break;
        case 'ancillary':
          html = generateSectionHTML('Ancillary Rooms', generateAncillaryRoomsHTML());
          filename += '_AncillaryRooms';
          break;
        case 'trucking':
          html = generateSectionHTML('Trucking', generateTruckingHTML());
          filename += '_Trucking';
          break;
        case 'location':
          html = generateSectionHTML('Location Information', generateLocationHTML());
          filename += '_Location';
          break;
        case 'all': {
          const emptyPattern = /No .* recorded\.|No .* assigned\.|No .* available\.|No .* in schedule\./;
          const addIfHasData = (title, contentFn) => {
            const content = contentFn();
            if (!emptyPattern.test(content)) {
              html += generateSectionHTML(title, content, true);
            }
          };
          html = generateTitlePageHTML();
          html += generateSectionHTML('Overview', generateOverviewHTML(), true);
          // Schedule uses its own multi-page generator
          const scheduleItems = event?.schedule || [];
          if (scheduleItems.length > 0) html += generateSchedulePagesHTML();
          addIfHasData('Event Contacts', generateContactsHTML);
          addIfHasData('Audio', generateAudioHTML);
          addIfHasData('Video', generateVideoHTML);
          addIfHasData('Lighting', generateLightingHTML);
          addIfHasData('Streaming', generateStreamingHTML);
          addIfHasData('Electrical', generateElectricHTML);
          html += generateDistroPagesHTML();
          addIfHasData('Scenic / Staging', generateScenicHTML);
          addIfHasData('Rigging', generateRiggingHTML);
          addIfHasData('Ancillary Rooms', generateAncillaryRoomsHTML);
          addIfHasData('Trucking', generateTruckingHTML);
          addIfHasData('Venue Information', generateLocationHTML);
          filename += '_Complete';
          break;
        }
        default:
          throw new Error('Unknown section');
      }

      const container = document.createElement('div');
      container.innerHTML = html;
      document.body.appendChild(container);

      const opt = {
        margin: [0.3, 0, 0.3, 0],
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true },
        jsPDF: { unit: 'in', format: 'letter', orientation: 'portrait' },
        pagebreak: { mode: ['avoid-all', 'css'] }
      };

      const worker = html2pdf().set(opt).from(container).toPdf();
      const pdfObj = await worker.get('pdf');
      const pdfBlob = pdfObj.output('blob');
      document.body.removeChild(container);

      const url = URL.createObjectURL(pdfBlob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${filename}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 1000);

      toast.success('PDF exported successfully');
    } catch (error) {
      console.error('PDF export error:', error);
      toast.error('Failed to export PDF');
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-primary"></div>
      </div>
    );
  }

  if (!event) return null;

  const statusBadge = getStatusBadge(event.status);

  return (
    <div className="space-y-6" data-testid="event-detail">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div className="flex items-start gap-4">
          <Button variant="ghost" size="icon" asChild>
            <Link to="/events">
              <ArrowLeft className="h-4 w-4" />
            </Link>
          </Button>
          <div>
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="text-3xl sm:text-4xl font-bold tracking-tight flex items-center gap-3">
                <Calendar className="h-8 w-8 text-emerald-400" />
                {event.event_name}
              </h1>
              {isViewer ? (
                <Badge variant="outline" className={statusBadge.color}>
                  {statusBadge.label}
                </Badge>
              ) : (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Badge variant="outline" className={`${statusBadge.color} cursor-pointer hover:opacity-80 transition-opacity`} data-testid="event-status-dropdown">
                      {statusBadge.label}
                      <ChevronDown className="h-3 w-3 ml-1" />
                    </Badge>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start">
                    {STATUS_OPTIONS.map(opt => (
                      <DropdownMenuItem
                        key={opt.value}
                        onClick={() => handleEventUpdate({ status: opt.value })}
                        className="flex items-center gap-2"
                        data-testid={`status-option-${opt.value}`}
                      >
                        <span className={`w-2 h-2 rounded-full ${opt.color.split(' ')[1]?.replace('/10', '')}`} />
                        <span className={event.status === opt.value ? 'font-bold' : ''}>{opt.label}</span>
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
            {event.flex_number && (
              <p className="text-muted-foreground mt-1 mono">Flex #{event.flex_number}</p>
            )}
            <div className="flex flex-wrap gap-4 mt-2 text-sm">
              {event.start_date && (
                <span className="flex items-center gap-1 text-muted-foreground">
                  <Clock className="h-4 w-4" />
                  {formatDate(event.start_date)}
                  {event.end_date && event.end_date !== event.start_date && (
                    <> - {formatDate(event.end_date)}</>
                  )}
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="flex gap-2 ml-12 sm:ml-0 flex-wrap items-center">
          {hasPendingChanges && (
            <Badge variant="outline" className="border-yellow-500/30 bg-yellow-500/10 text-yellow-400 animate-pulse">
              Saving...
            </Badge>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" disabled={exporting} data-testid="export-pdf-btn">
                {exporting ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <FileDown className="h-4 w-4 mr-2" />
                )}
                Export PDF
                <ChevronDown className="h-4 w-4 ml-2" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>Export Options</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => exportPDF('all')}>
                <FileText className="h-4 w-4 mr-2" />
                Complete Show Package
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuLabel className="text-xs text-muted-foreground">Individual Sections</DropdownMenuLabel>
              <DropdownMenuItem onClick={() => exportPDF('titlepage')}>
                Title Page
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => exportPDF('overview')}>
                Overview
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => exportPDF('schedule')}>
                Schedule
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => exportPDF('schedule-flow')}>
                Schedule - Flow
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => exportPDF('contacts')}>
                Contacts
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => exportPDF('audio')}>
                Audio
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => exportPDF('video')}>
                Video
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => exportPDF('lighting')}>
                Lighting
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => exportPDF('streaming')}>
                Streaming
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => exportPDF('electric')}>
                Electric
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => exportPDF('scenic')}>
                Scenic / Staging
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => exportPDF('rigging')}>
                Rigging
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => exportPDF('ancillary')}>
                Ancillary Rooms
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => exportPDF('trucking')}>
                Trucking
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => exportPDF('location')}>
                Location Information
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          {!isViewer && (
            <Button
              variant="outline"
              onClick={() => {
                setCopyForm({ flex_number: '', start_date: event.start_date || '', end_date: event.end_date || '' });
                setShowCopyDialog(true);
              }}
              data-testid="copy-event-btn"
            >
              <Copy className="h-4 w-4 mr-2" />
              Copy Event
            </Button>
          )}
          {!isViewer && (
            <>
              <Button variant="outline" asChild data-testid="edit-event-btn">
                <Link to={`/events/${id}/edit`}>
                  <Pencil className="h-4 w-4 mr-2" />
                  Edit
                </Link>
              </Button>
              <div className="w-px h-6 bg-border/50 mx-1" />
              {isAdmin && (
                <Button
                  variant="outline"
                  onClick={() => setShowDelete(true)}
                  data-testid="delete-event-btn"
                >
                  <Trash2 className="h-4 w-4 mr-2 text-destructive" />
                  Delete
                </Button>
              )}
            </>
          )}
        </div>
      </div>

      {/* Quick Info Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {event.client_name && (
          <Link to={`/organizations/${event.client_id}`}>
            <Card className="border-border/50 bg-card/50 hover:bg-card/80 transition-colors">
              <CardContent className="p-4 flex items-center gap-3">
                <div className="p-2 rounded-lg bg-cyan-500/10">
                  <Building2 className="h-5 w-5 text-cyan-400" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground uppercase">Client</p>
                  <p className="font-medium">{event.client_name}</p>
                </div>
              </CardContent>
            </Card>
          </Link>
        )}
        {(event.location_name || event.venue_name) && (
          <Link to={`/locations/${event.location_id || event.venue_id}`}>
            <Card className="border-border/50 bg-card/50 hover:bg-card/80 transition-colors">
              <CardContent className="p-4 flex items-center gap-3">
                <div className="p-2 rounded-lg bg-amber-500/10">
                  <MapPin className="h-5 w-5 text-amber-400" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground uppercase">Location</p>
                  <p className="font-medium">{event.location_name || event.venue_name}</p>
                </div>
              </CardContent>
            </Card>
          </Link>
        )}
        {event.project_manager_name && (
          <Link to={`/contacts/${event.project_manager_id}`}>
            <Card className="border-border/50 bg-card/50 hover:bg-card/80 transition-colors">
              <CardContent className="p-4 flex items-center gap-3">
                <div className="p-2 rounded-lg bg-cyan-500/10">
                  <User className="h-5 w-5 text-cyan-400" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground uppercase">Project Manager</p>
                  <p className="font-medium">{event.project_manager_name}</p>
                </div>
              </CardContent>
            </Card>
          </Link>
        )}
        {event.account_exec_name && (
          <Link to={`/contacts/${event.account_exec_id}`}>
            <Card className="border-border/50 bg-card/50 hover:bg-card/80 transition-colors">
              <CardContent className="p-4 flex items-center gap-3">
                <div className="p-2 rounded-lg bg-emerald-500/10">
                  <User className="h-5 w-5 text-emerald-400" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground uppercase">Account Exec</p>
                  <p className="font-medium">{event.account_exec_name}</p>
                </div>
              </CardContent>
            </Card>
          </Link>
        )}
      </div>

      {/* Main Content */}
      <div className="space-y-6">
        <div className="space-y-6">
          <Tabs defaultValue="overview" className="w-full">
            <TabsList className="bg-muted/50 flex-wrap h-auto gap-1 p-1">
              <TabsTrigger value="overview" className="text-xs">
                Overview
                <ContentIndicator hasContent={hasTabContent(event, contacts, 'overview')} />
              </TabsTrigger>
              <TabsTrigger value="schedule" className="text-xs">
                Schedule
                <ContentIndicator hasContent={hasTabContent(event, contacts, 'schedule')} />
              </TabsTrigger>
              <TabsTrigger value="contacts" className="text-xs">
                Contacts
                <ContentIndicator hasContent={hasTabContent(event, contacts, 'contacts')} />
              </TabsTrigger>
              <TabsTrigger value="audio" className="text-xs">
                Audio
                <ContentIndicator hasContent={hasTabContent(event, contacts, 'audio')} />
              </TabsTrigger>
              <TabsTrigger value="video" className="text-xs">
                Video
                <ContentIndicator hasContent={hasTabContent(event, contacts, 'video')} />
              </TabsTrigger>
              <TabsTrigger value="lighting" className="text-xs">
                Lighting
                <ContentIndicator hasContent={hasTabContent(event, contacts, 'lighting')} />
              </TabsTrigger>
              <TabsTrigger value="streaming" className="text-xs">
                Streaming
                <ContentIndicator hasContent={hasTabContent(event, contacts, 'streaming')} />
              </TabsTrigger>
              <TabsTrigger value="electric" className="text-xs">
                Electric
                <ContentIndicator hasContent={hasTabContent(event, contacts, 'electric')} />
              </TabsTrigger>
              <TabsTrigger value="rigging" className="text-xs">
                Rigging
                <ContentIndicator hasContent={!!event?.rigging} />
              </TabsTrigger>
              <TabsTrigger value="scenic" className="text-xs">
                Scenic/Staging
                <ContentIndicator hasContent={hasTabContent(event, contacts, 'scenic')} />
              </TabsTrigger>
              <TabsTrigger value="ancillary-rooms" className="text-xs">
                Ancillary Rooms
                <ContentIndicator hasContent={(event?.ancillary_rooms || []).length > 0} />
              </TabsTrigger>
              <TabsTrigger value="trucking" className="text-xs">
                Trucking
                <ContentIndicator hasContent={hasTabContent(event, contacts, 'trucking')} />
              </TabsTrigger>
              <TabsTrigger value="logistics" className="text-xs">
                Logistics Letter
              </TabsTrigger>
              <TabsTrigger value="call-sheets" className="text-xs">Call Sheets</TabsTrigger>
              <TabsTrigger value="post" className="text-xs">
                Post
                <ContentIndicator hasContent={!!event?.post_event_notes} />
              </TabsTrigger>
              <TabsTrigger value="activity" className="text-xs">
                Activity
              </TabsTrigger>
            </TabsList>

            {/* Overview Tab - Editable */}
            <TabsContent value="overview" className="mt-6">
              <EditableOverviewTab event={event} onUpdate={handleEventUpdate} />
            </TabsContent>

            {/* Schedule Tab - Editable */}
            <TabsContent value="schedule" className="mt-6">
              <EditableScheduleTab event={event} onUpdate={handleEventUpdate} />
            </TabsContent>

            {/* Call Sheets Tab */}
            <TabsContent value="call-sheets" className="mt-6">
              <Card className="border-border/50 bg-card/50">
                <CardContent className="pt-6">
                  <CallSheetsTab eventId={id} event={event} />
                </CardContent>
              </Card>
            </TabsContent>

            {/* Post Event Tab */}
            <TabsContent value="post" className="mt-6">
              <EditablePostTab event={event} onUpdate={handleEventUpdate} />
            </TabsContent>

            {/* Contacts Tab - Editable */}
            <TabsContent value="contacts" className="mt-6">
              <EditableContactsTab 
                eventId={id} 
                contacts={contacts} 
                onContactsChange={setContacts} 
              />
            </TabsContent>

            {/* Audio Tab - Editable */}
            <TabsContent value="audio" className="mt-6">
              <EditableAudioTab event={event} onUpdate={handleEventUpdate} />
            </TabsContent>

            {/* Video Tab - Editable */}
            <TabsContent value="video" className="mt-6">
              <EditableVideoTab event={event} onUpdate={handleEventUpdate} />
            </TabsContent>

            {/* Lighting Tab - Editable */}
            <TabsContent value="lighting" className="mt-6">
              <EditableLightingTab event={event} onUpdate={handleEventUpdate} />
            </TabsContent>

            {/* Streaming Tab - Editable */}
            <TabsContent value="streaming" className="mt-6">
              <EditableStreamingTab event={event} onUpdate={handleEventUpdate} />
            </TabsContent>

            {/* Electric Tab - Editable */}
            <TabsContent value="electric" className="mt-6">
              <EditableElectricTab event={event} onUpdate={handleEventUpdate} />
            </TabsContent>

            {/* Rigging Tab */}
            <TabsContent value="rigging" className="mt-6">
              <EditableRiggingTab event={event} onUpdate={handleEventUpdate} />
            </TabsContent>

            {/* Scenic/Staging Tab - Editable */}
            <TabsContent value="scenic" className="mt-6">
              <EditableScenicTab event={event} onUpdate={handleEventUpdate} />
            </TabsContent>

            {/* Ancillary Rooms Tab */}
            <TabsContent value="ancillary-rooms" className="mt-6">
              <EditableAncillaryRoomsTab event={event} onUpdate={handleEventUpdate} />
            </TabsContent>

            {/* Trucking Tab */}
            <TabsContent value="trucking" className="mt-6">
              <Card className="border-border/50 bg-card/50">
                <CardHeader>
                  <CardTitle className="text-lg font-semibold flex items-center gap-2">
                    <Truck className="h-5 w-5 text-orange-400" />
                    Trucking Calls
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {(() => {
                    // Get all schedule items with Trucking type
                    const truckingItems = (event?.schedule || [])
                      .filter(item => (item.schedule_types || []).includes('Trucking'))
                      .sort((a, b) => {
                        const dateA = a.date || '';
                        const dateB = b.date || '';
                        if (dateA !== dateB) return dateA.localeCompare(dateB);
                        const timeA = a.time || '';
                        const timeB = b.time || '';
                        return timeA.localeCompare(timeB);
                      });

                    if (truckingItems.length === 0) {
                      return (
                        <p className="text-muted-foreground text-center py-8">
                          No trucking calls scheduled for this event.
                        </p>
                      );
                    }

                    // Helper to get contact phone
                    const getContactPhone = (contactId) => {
                      const contact = allContacts.find(c => c.id === contactId);
                      return contact?.mobile || contact?.phone || null;
                    };

                    // Helper to format date
                    const formatTruckingDate = (dateStr) => {
                      if (!dateStr) return '-';
                      try {
                        return format(parseISO(dateStr), 'EEE, MMM d, yyyy');
                      } catch {
                        return dateStr;
                      }
                    };

                    // Helper to format time
                    const formatTruckingTime = (timeStr) => {
                      if (!timeStr) return '-';
                      try {
                        const [hours, minutes] = timeStr.split(':');
                        const hour = parseInt(hours);
                        const ampm = hour >= 12 ? 'PM' : 'AM';
                        const hour12 = hour % 12 || 12;
                        return `${hour12}:${minutes} ${ampm}`;
                      } catch {
                        return timeStr;
                      }
                    };

                    return (
                      <div className="space-y-4">
                        {truckingItems.map((item, index) => (
                          <div key={item.id || index} className="border border-border/50 rounded-lg p-4 bg-background/30">
                            {/* Header with date/time and action */}
                            <div className="flex items-start justify-between mb-3">
                              <div>
                                <p className="font-medium text-base">{item.action || 'Trucking Call'}</p>
                                <div className="flex items-center gap-3 text-sm text-muted-foreground mt-1">
                                  <span className="flex items-center gap-1">
                                    <Calendar className="h-3.5 w-3.5" />
                                    {formatTruckingDate(item.date)}
                                  </span>
                                  <span className="flex items-center gap-1">
                                    <Clock className="h-3.5 w-3.5" />
                                    {formatTruckingTime(item.time)}
                                  </span>
                                </div>
                              </div>
                              <Badge variant="outline" className={
                                item.status === 'Confirmed' 
                                  ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-400'
                                  : 'border-yellow-500/30 bg-yellow-500/10 text-yellow-400'
                              }>
                                {item.status || 'Tentative'}
                              </Badge>
                            </div>

                            {/* Drivers */}
                            {item.truck_assignments && item.truck_assignments.length > 0 ? (
                              <div className="space-y-2">
                                <p className="text-xs text-muted-foreground uppercase tracking-wider">Assigned Drivers</p>
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                                  {item.truck_assignments.map((assignment, idx) => (
                                    <div key={idx} className="flex items-start gap-3 p-3 bg-muted/30 rounded-lg">
                                      <div className="p-2 rounded-full bg-orange-500/10">
                                        <User className="h-4 w-4 text-orange-400" />
                                      </div>
                                      <div className="flex-1 min-w-0">
                                        <p className="font-medium text-sm truncate">{assignment.driver_name || 'Unknown Driver'}</p>
                                        {getContactPhone(assignment.driver_id) && (
                                          <p className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
                                            <Phone className="h-3 w-3" />
                                            {getContactPhone(assignment.driver_id)}
                                          </p>
                                        )}
                                        <p className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
                                          <Truck className="h-3 w-3" />
                                          {assignment.truck || 'No truck assigned'}
                                        </p>
                                        {assignment.notes && (
                                          <p className="text-xs text-muted-foreground mt-1 italic">{assignment.notes}</p>
                                        )}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            ) : (
                              <p className="text-sm text-muted-foreground italic">No drivers assigned yet</p>
                            )}

                            {/* Notes */}
                            {item.notes && (
                              <div className="mt-3 pt-3 border-t border-border/30">
                                <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Notes</p>
                                <p className="text-sm">{item.notes}</p>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    );
                  })()}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Logistics Letter Tab */}
            <TabsContent value="logistics" className="mt-6">
              <LogisticsLetterTab 
                event={event} 
                contacts={contacts}
                allContacts={allContacts}
                location={location}
                onUpdate={handleEventUpdate} 
              />
            </TabsContent>

            {/* Activity Tab */}
            <TabsContent value="activity" className="mt-6">
              <Card className="border-border/50 bg-card/50">
                <CardHeader>
                  <CardTitle className="text-lg font-semibold flex items-center gap-2">
                    <History className="h-5 w-5 text-muted-foreground" />
                    Activity Log
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {activities.length > 0 ? (
                    <div className="space-y-4">
                      {activities.map((activity) => {
                        // Parse changes to show what was updated
                        const getChangeSummary = () => {
                          if (!activity.changes || typeof activity.changes !== 'object') return null;
                          const changedFields = Object.keys(activity.changes).filter(key => 
                            // Filter out complex nested objects and internal fields
                            !['schedule', 'audio_inputs', 'audio_outputs', 'video_inputs', 'video_outputs', 
                              'streaming', 'electric', 'scenic_staging', 'event_contacts', 'updated_at',
                              'created_at', 'id'].includes(key) &&
                            activity.changes[key] !== '' && 
                            activity.changes[key] !== null
                          );
                          
                          // Check for special cases
                          const hasScheduleChanges = 'schedule' in activity.changes;
                          const hasAudioChanges = ['audio_overview', 'audio_notes'].some(k => k in activity.changes && activity.changes[k]);
                          const hasVideoChanges = ['video_overview', 'video_notes'].some(k => k in activity.changes && activity.changes[k]);
                          const hasLightingChanges = 'lighting_notes' in activity.changes && activity.changes.lighting_notes;
                          const hasStreamingChanges = 'streaming' in activity.changes;
                          const hasElectricChanges = 'electric' in activity.changes;
                          
                          const summaryParts = [];
                          
                          // Add human-readable field names
                          const fieldLabels = {
                            event_name: 'Event Name',
                            flex_number: 'Flex #',
                            start_date: 'Start Date',
                            end_date: 'End Date',
                            status: 'Status',
                            description: 'Description',
                            room: 'Room',
                            client_id: 'Client',
                            venue_id: 'Location',
                            project_manager_id: 'Project Manager',
                            account_exec_id: 'Account Exec',
                            rigging: 'Rigging',
                          };
                          
                          changedFields.forEach(field => {
                            if (fieldLabels[field]) {
                              summaryParts.push(fieldLabels[field]);
                            }
                          });
                          
                          if (hasScheduleChanges) summaryParts.push('Schedule');
                          if (hasAudioChanges) summaryParts.push('Audio');
                          if (hasVideoChanges) summaryParts.push('Video');
                          if (hasLightingChanges) summaryParts.push('Lighting');
                          if (hasStreamingChanges) summaryParts.push('Streaming');
                          if (hasElectricChanges) summaryParts.push('Electric');
                          
                          return summaryParts.length > 0 ? summaryParts : null;
                        };
                        
                        const changeSummary = getChangeSummary();
                        
                        return (
                          <div key={activity.id} className="flex items-start gap-4 p-4 rounded-lg bg-muted/30 border border-border/30">
                            <div className="p-2 rounded-full bg-primary/10 shrink-0">
                              <History className="h-4 w-4 text-primary" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center flex-wrap gap-2 mb-1">
                                <Badge variant="outline" className={`text-xs capitalize ${
                                  activity.action === 'created' ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-400' :
                                  activity.action === 'updated' ? 'border-blue-500/30 bg-blue-500/10 text-blue-400' :
                                  activity.action === 'deleted' ? 'border-red-500/30 bg-red-500/10 text-red-400' :
                                  ''
                                }`}>
                                  {activity.action}
                                </Badge>
                                {activity.created_by_name && (
                                  <span className="text-sm font-medium text-foreground">
                                    {activity.created_by_name}
                                  </span>
                                )}
                                <span className="text-xs text-muted-foreground">
                                  {formatDistanceToNow(new Date(activity.created_at), { addSuffix: true })}
                                </span>
                              </div>
                              <p className="text-sm text-muted-foreground">{activity.description}</p>
                              {changeSummary && changeSummary.length > 0 && (
                                <div className="flex flex-wrap gap-1.5 mt-2">
                                  {changeSummary.map((field, idx) => (
                                    <span key={idx} className="text-xs px-2 py-0.5 rounded-full bg-background border border-border/50">
                                      {field}
                                    </span>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="text-muted-foreground text-center py-8">
                      No activity recorded for this event yet.
                    </p>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDelete} onOpenChange={setShowDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Event</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{event.event_name}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="confirm-delete-btn"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Copy Event Dialog */}
      <Dialog open={showCopyDialog} onOpenChange={setShowCopyDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Copy Event</DialogTitle>
            <DialogDescription>
              Create a copy of "{event.event_name}" with new dates. Driver assignments will be cleared and status set to Inquiry.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="copy-flex">Flex # *</Label>
              <Input
                id="copy-flex"
                value={copyForm.flex_number}
                onChange={(e) => setCopyForm({ ...copyForm, flex_number: e.target.value })}
                placeholder="e.g. 26-0300"
                data-testid="copy-flex-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="copy-start">Start Date *</Label>
                <Input
                  id="copy-start"
                  type="date"
                  value={copyForm.start_date}
                  onChange={(e) => setCopyForm({ ...copyForm, start_date: e.target.value })}
                  data-testid="copy-start-date"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="copy-end">End Date</Label>
                <Input
                  id="copy-end"
                  type="date"
                  value={copyForm.end_date}
                  onChange={(e) => setCopyForm({ ...copyForm, end_date: e.target.value })}
                  data-testid="copy-end-date"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCopyDialog(false)}>Cancel</Button>
            <Button onClick={handleCopyEvent} disabled={copying} data-testid="confirm-copy-btn">
              {copying ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Copying...</> : 'Copy Event'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EventDetail;
